import React, { useEffect, useState } from "react";
import {
  Avatar,
  Grid,
  Paper,
  Stack,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { userSelector } from "../user-redux/selector";
import Key from "../../clientVariables.json";
import CandidateDashboardListSkeleton from "./CandidateDashboardListSkeleton";

const SUBMITTED_STATUS_IDS = [3, 5, 7];

const Candidatedashboardsubmissions = () => {
  const { user } = useSelector(userSelector);
  const navigate = useNavigate();
  const theme = useTheme();

  const [candidateSubmissions, setCandidateSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const isMobileSmall = useMediaQuery(theme.breakpoints.down("xs")); // Very small mobile devices
  const isMobile = useMediaQuery(theme.breakpoints.between("xs", "sm")); // Regular mobile devices
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // Tablets
  const isDesktop = useMediaQuery(theme.breakpoints.up("md")); // Desktops and larger
  const isDesktopSmall = useMediaQuery(theme.breakpoints.between("md", "lg")); // Small desktops
  const isDesktopLarge = useMediaQuery(theme.breakpoints.up("lg")); // Large desktops

  useEffect(() => {
    getSubmissionsCandidate();
  }, []);

  const getSubmissionsCandidate = async () => {
    try {
      const response = await fetch(
        `${Key.domain}/candidate/${user?.candidate_id}/submissions/0/200`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await response.json();
      setCandidateSubmissions(data?.data?.submissions || []);
    } catch (error) {
      console.error("Error fetching submissions:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSubmissions = candidateSubmissions.filter((job) =>
    SUBMITTED_STATUS_IDS.includes(Number(job.bench_status_id))
  );

  const handleSubmissionClick = (job) => {
    if (!job?.id) return;
    navigate("/candidates-list", {
      state: { selectedTab: 2, selectedSubmissionId: job.id },
    });
  };

  // Render Individual Submission Card
  const renderSubmissionCard = (job, index) => (
    <Grid item xs={12} key={job?.id ?? index}>
      <Paper
        elevation={3}
        onClick={() => handleSubmissionClick(job)}
        sx={{
          borderRadius: "8px",
          padding: "12px",
          border: "1px solid #ddd",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          cursor: "pointer",
          transition: "transform 0.3s ease-in-out",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          {/* Avatar Section */}
          <Grid item xs={2} sx={{ display: "flex", justifyContent: "center" }}>
            <Avatar sx={{
              height: isDesktopSmall
                ? "40px"
                : isMobile || isTablet
                  ? "50px"
                  : "50px",
              width: isDesktopSmall ? "40px" : isMobile ? "50px" : "50px",
            }}
            >{/* Optionally, use an image */}</Avatar>
          </Grid>

          {/* Job Details */}
          <Grid item xs={10}>
            <Stack spacing={0.8} sx={{ marginBottom: "4px" }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body1"
                  title={job.jobtitle}
                  sx={{
                    fontWeight: "bold",
                    fontSize: isMobileSmall
                      ? "10px"
                      : isMobile || isDesktopSmall
                        ? "10px"
                        : "15px",
                    color: "#333333",
                    lineHeight: "1.2",

                    // make it occupy available space, then ellipsize
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  }}
                >
                  {job.jobtitle || "—"}
                </Typography>
              </Box>

              {/* Move Submission Status to the Right Section */}
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  title={job.company_name}
                  variant="body2"
                  sx={{
                    color: "#555", fontSize: "13px", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {job.company_name || "N/A"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ color: "#555", fontSize: "13px" }}>
                  {job?.location ? job.location : ((parseInt(job.remote) === 1 || job.location === "") ? "Remote" : "N/A")}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    fontSize: "12px",
                    border: `1.5px solid ${job?.pStatusName === "Rejected" ? "rgb(255, 0, 0)" : "rgb(45, 149, 150)"
                      }`,
                    borderRadius: "4px",
                    backgroundColor:
                      job?.pStatusName === "Rejected" ? "rgba(255, 0, 0, 0.1)" : "rgb(210, 233, 233)",
                    color: job?.pStatusName === "Rejected" ? "rgb(255, 0, 0)" : "rgb(45, 149, 150)",
                    padding: "2px 6px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor:
                        job?.pStatusName === "Rejected" ? "rgb(255, 0, 0)" : "rgb(45, 149, 150)",
                      color: "#fff",
                    },
                  }}
                >
                  {job?.pStatusName || "Pending"}
                </Typography>

              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );

  return (
    <Box
      sx={{
        padding: "16px",
        backgroundColor: "#f4f6f8",
        minHeight: "100%",
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      {loading
        ? <CandidateDashboardListSkeleton showStatusChip />
        : filteredSubmissions.length > 0 ? (
          <Grid container spacing={2}>
            {filteredSubmissions.slice(0, 4).map(renderSubmissionCard)}
          </Grid>
        ) : (
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              marginTop: "20px",
              color: "#666666",
              fontSize: "16px",
            }}
          >
            No submissions found
          </Typography>
        )}
    </Box>
  );
};

export default Candidatedashboardsubmissions;
